"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@/providers/auth-provider";
import { useUiStore } from "@/store/ui-store";
import { useIsMobile } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils/cn";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar } from "@/components/ui/avatar";
import {
  LayoutDashboard,
  Users,
  BarChart3,
  MessageSquare,
  GraduationCap,
  Calendar,
  BookOpen,
  Shield,
  ChevronLeft,
  Search,
  PanelsTopLeft,
} from "lucide-react";
import { usePermissions } from "@/hooks/use-auth";

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  permission?: string;
}

const navItems: NavItem[] = [
  { label: "Home", href: "/home", icon: LayoutDashboard },
  { label: "Profile", href: "/profile", icon: Users },
  { label: "Messages", href: "/messages", icon: MessageSquare },
  { label: "Notifications", href: "/notifications", icon: BarChart3 },
  { label: "Network", href: "/network", icon: Users },
  { label: "My Learning", href: "/learning/my-learning", icon: GraduationCap },
  { label: "My Events", href: "/my-events", icon: Calendar },
  { label: "Saved", href: "/saved", icon: BookOpen },
  { label: "Activity", href: "/activity", icon: BarChart3 },
];

const adminItems: NavItem[] = [
  { label: "Admin", href: "/admin", icon: Shield },
  { label: "Analytics", href: "/analytics", icon: BarChart3 },
  { label: "Users", href: "/users", icon: Users },
];

export function Sidebar() {
  const pathname = usePathname();
  const { user } = useAuth();
  const { sidebarOpen, setSidebarOpen } = useUiStore();
  const isMobile = useIsMobile();
  const { role } = usePermissions();
  const isAdmin = role === "admin" || role === "super_admin";

  return (
    <>
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-overlay backdrop-blur-sm transition-opacity"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 z-50 h-full border-r border-border bg-surface transition-transform duration-3 ease-out",
          isMobile ? "w-64" : "w-60",
          isMobile
            ? sidebarOpen
              ? "translate-x-0"
              : "-translate-x-full pointer-events-none"
            : sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        aria-label="Main navigation"
      >
        <div className="flex h-14 items-center justify-between px-4 border-b border-border/60">
          <Link
            href="/home"
            className="flex items-center gap-2.5 text-sm font-semibold tracking-tight text-fg"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-3 bg-fg text-background text-xs font-bold">
              HR
            </span>
            <span className="font-heading">Shakti</span>
          </Link>
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
              className="h-11 w-11"
              aria-label="Close sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="p-3">
          <form onSubmit={(e) => e.preventDefault()} className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-tertiary" />
            <Input
              placeholder="Search..."
              className="h-10 w-full pl-8 rounded-3"
            />
          </form>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isExact = pathname === item.href;

            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "flex items-center gap-3 rounded-3 px-3 py-2 text-3 font-medium transition-all duration-3 ease mb-0.5",
                  isExact
                    ? "bg-primary-50 text-primary-700"
                    : "text-fg-tertiary hover:bg-surface-tertiary hover:text-fg-secondary"
                )}
              >
                <Icon className={cn("h-4 w-4 shrink-0", isExact ? "text-primary-500" : "text-fg-tertiary")} />
                <span>{item.label}</span>
              </Link>
            );
          })}

          {isAdmin && (
            <>
              <div className="my-3 border-t border-border/60" />
              <p className="px-3 mb-1.5 text-1 font-semibold uppercase tracking-widest text-fg-quaternary">
                Administration
              </p>
              {adminItems.map((item) => {
                const Icon = item.icon;
                const isExact = pathname === item.href;

                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      "flex items-center gap-3 rounded-3 px-3 py-2 text-3 font-medium transition-all duration-3 ease mb-0.5",
                      isExact
                        ? "bg-primary-50 text-primary-700"
                        : "text-fg-tertiary hover:bg-surface-tertiary hover:text-fg-secondary"
                    )}
                  >
                    <Icon className={cn("h-4 w-4 shrink-0", isExact ? "text-primary-500" : "text-fg-tertiary")} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        <div className="border-t border-border/60 p-3 space-y-2">
          {user && (
            <Link href="/profile" className="flex items-center gap-3 rounded-3 px-3 py-2 text-3 font-medium text-fg-tertiary hover:bg-surface-tertiary hover:text-fg-secondary transition-colors duration-3 ease">
              <Avatar src={user.avatarUrl} alt={`${user.firstName} ${user.lastName}`} size="sm" fallback={user.firstName?.charAt(0)} />
              <div className="min-w-0">
                <p className="truncate text-2 font-medium text-fg">{user.firstName} {user.lastName}</p>
                <p className="truncate text-2 text-fg-tertiary">{user.email}</p>
              </div>
            </Link>
          )}
          <Button variant="ghost" size="sm" asChild className="w-full justify-start gap-3 h-9 text-2 font-medium">
            <Link href="/">
              <PanelsTopLeft className="h-4 w-4" />
              Back to site
            </Link>
          </Button>
        </div>
      </aside>
    </>
  );
}
